import { ChangeDetectionStrategy, Component, DestroyRef, effect, ElementRef, Input, OnInit, signal, viewChild } from '@angular/core';
import { BomTreeItemVm } from '../../models/bom-tree-item.view-model';
import { BomTreeStore } from '../../bom-tree.store';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { formatAmount, formatAmountInput } from '../../../../../utils/format-amount';

@Component({
  selector: 'bom-tree-node',
  templateUrl: './bom-tree-node.component.html',
  styleUrl: './bom-tree-node.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [],
  host: {
    '[style.margin-left.px]': 'marginLeftPx',
  }
})
export class BomTreeNodeComponent implements OnInit {
  @Input({ required: true }) item!: BomTreeItemVm;
  @Input({ required: true }) selectedNodeId: string | undefined;
  @Input() depthLevel = 0;

  // one field edits at a time, held here rather than in the store, so Escape and blur leave the tree untouched (0124)
  readonly editing = signal<'name' | 'amount' | undefined>(undefined);
  // only one editor is open at a time, so both share one ref (the page's attributeInput precedent)
  readonly editInput = viewChild<ElementRef<HTMLInputElement>>('editInput');

  marginLeftPx = 0;

  formatAmount = formatAmount;
  formatAmountInput = formatAmountInput;

  constructor(
    private store: BomTreeStore,
    private destroyRef: DestroyRef
  ) {
    // the input only exists while editing — focus it as soon as it appears, selected so typing replaces it
    effect(() => {
      const input = this.editInput();

      if (input) {
        input.nativeElement.focus();
        input.nativeElement.select();
      }
    });
  }

  // the quantity on the relation leading here: the root has none, and a node still hydrating or not
  // yet created has no relation to patch
  get amountEditable(): boolean {
    return this.depthLevel > 0 && !this.item.hydrating && !!this.item.data.bomItemId && !!this.item.data.bomRelationId;
  }

  ngOnInit(): void {
    this.initMargin();
  }

  onRowClick(): void {
    this.store.selectNode(this.item.id);
  }

  // the description, when it is not the name again (an in-house part imported after 0094 carries the same text twice)
  hint(): string | undefined {
    const { name, description } = this.item.data;

    return description && description.trim() !== name.trim() ? description : undefined;
  }

  // the native tooltip: part number and description, each on its own line, each only when present
  tooltip(): string | null {
    const { partNumber, description } = this.item.data;

    const lines = [
      partNumber ? `Numer części: ${partNumber}` : undefined,
      description ? `Opis: ${description}` : undefined
    ].filter(line => line);

    return lines.length > 0 ? lines.join('\n') : null;
  }

  startEditing(field: 'name' | 'amount'): void {
    // a double-click inside an open editor (selecting a word) must not switch editors
    if (this.editing()) {
      return;
    }

    this.editing.set(field);
  }

  // the amount cell is its own double-click target; on a row without an editable amount it does nothing at all
  onAmountDoubleClick(event: MouseEvent): void {
    event.stopPropagation();

    if (this.amountEditable) {
      this.startEditing('amount');
    }
  }

  stopEditing(): void {
    this.editing.set(undefined);
  }

  // Enter closes the editor first, so the blur that follows finds nothing to close and sends nothing
  saveName(value: string): void {
    this.stopEditing();
    this.store.renameNode(this.item.id, value).pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
  }

  saveAmount(value: string): void {
    this.stopEditing();
    this.store.setNodeAmount(this.item.id, value).pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
  }

  onContextMenu(event: MouseEvent): void {
    // native context menu stays available inside the edit inputs (copy/paste)
    if (this.editing()) {
      return;
    }

    /**
     * Right-click at (almost) the same point again: close our menu and let the native one through.
     * The menu's top-left corner sits exactly under the opening click, so a repeated click a few px
     * up/left hits the row, not the panel — without the tolerance the bypass would depend on which
     * side of the corner the click lands.
     */
    const openAt = this.store.state.contextMenu?.position;
    const samePoint = openAt
      && Math.abs(openAt.x - event.clientX) < 10
      && Math.abs(openAt.y - event.clientY) < 10;

    if (samePoint) {
      this.store.closeContextMenu();
      return;
    }

    event.preventDefault();

    // right-click selects, like left-click — the menu acts on the row under the cursor
    this.store.selectNode(this.item.id);
    this.store.openContextMenu(this.item.id, { x: event.clientX, y: event.clientY });
  }

  private initMargin(): void {
    if (this.depthLevel === 0) {
      return;
    }

    this.marginLeftPx = 20;
  }
}
